import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Plus, Map, CheckSquare, MessageSquare, ClipboardList, ArrowRight, House } from "lucide-react";
import { HostPortrait } from "./HostSeal";
import { RegistrationForm } from "./RegistrationForm";
import { Reveal, SectionLabel, ParallaxBg } from "./Chrome";
import { HOST, MODULES, TRANSFORMS, TAKEAWAYS, FAQS, COMPLIANCE, LOGO_URL, scrollToId } from "../content";

const TICONS = { Map, CheckSquare, MessageSquare, ClipboardList };

const AccordionItem = ({ num, title, body, open, onToggle, testId }) => (
  <div className="border-b border-white/10">
    <button onClick={onToggle} data-testid={testId} className="w-full flex items-center gap-5 py-6 text-left group">
      <span className="font-mono-c text-sm text-[#5D8FC4]">{num}</span>
      <span className={`flex-1 font-black uppercase tracking-tight text-base md:text-lg transition-colors duration-300 ${open ? "text-[#5D8FC4]" : "text-[#EDF2F7] group-hover:text-white"}`}>
        {title}
      </span>
      <Plus className={`w-5 h-5 text-[#5D8FC4] transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
    </button>
    <div className={`accordion-body ${open ? "open" : ""}`}>
      <div>
        <p className="pb-6 pl-10 pr-4 text-sm text-white/55 leading-[1.75] max-w-2xl">{body}</p>
      </div>
    </div>
  </div>
);

export const Curriculum = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="px-6 md:px-12 xl:px-20 py-28 md:py-36" data-testid="curriculum-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <SectionLabel num="004" text="The Curriculum" />
          <Reveal>
            <h2 className="mt-6 uppercase font-black tracking-tight text-3xl md:text-5xl max-w-2xl">Get a Sneak Peek Into the Webinar</h2>
          </Reveal>
        </div>
        <p className="font-mono-c text-[11px] tracking-[0.25em] text-white/40 uppercase shrink-0">5 Modules · 45 Minutes Total</p>
      </div>
      <div className="mt-12 border-t border-white/10">
        {MODULES.map((m, i) => (
          <AccordionItem
            key={i}
            num={String(i + 1).padStart(2, "0")}
            title={m.title}
            body={m.body}
            open={open === i}
            onToggle={() => setOpen(open === i ? -1 : i)}
            testId={`curriculum-accordion-${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export const Host = () => (
  <section id="host" className="px-6 md:px-12 xl:px-20 py-28 md:py-36 bg-white/[0.015]" data-testid="host-section">
    <SectionLabel num="005" text="Your Host" />
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-12 lg:gap-20 items-start">
      <Reveal><HostPortrait /></Reveal>
      <div>
        <Reveal>
          <h2 className="uppercase font-black tracking-tight text-3xl md:text-5xl leading-[1.02]">
            Guidance From Someone Who Works <span className="text-[#5D8FC4]">Inside</span> the Process.
          </h2>
          <div className="h-px bg-[#5D8FC4]/50 w-24 my-8" />
          <div className="space-y-5 text-white/55 leading-[1.8] max-w-2xl">
            <p>
              I'm Oscar Tobar, and I've spent my career at Great Western Funding working directly with veterans, active duty
              service members, and their families across Florida. Day in and day out, I sit inside the VA loan
              process — entitlement calculations, certificates of eligibility, funding fee exemptions, appraisals — the
              machinery most buyers never get to see.
            </p>
            <p>
              What I've learned is simple: veterans don't struggle with VA loans because the benefit is complicated. They
              struggle because nobody takes 45 minutes to explain it before the quotes start flying. Most are treated like
              conventional buyers from the first phone call, told to bring a down payment their benefit may not require, and
              shopped to multiple lenders before anyone explains what their entitlement actually covers.
            </p>
            <p>
              This webinar exists to fix that sequence. You learn how your purchasing power is calculated first — in plain
              English, with no application, no hard credit pull, and no obligation — so that every conversation you have
              afterward happens on your terms.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-xl" data-testid="credential-grid">
            {[["COMPANY", HOST.company], ["NMLS#", HOST.nmls], ["PHONE", HOST.phone], ["LICENSED IN", HOST.states]].map(([label, value]) => (
              <div key={label} className="bg-[#0b0b0b] p-5">
                <p className="font-mono-c text-[10px] tracking-[0.25em] text-white/35">{label}</p>
                <p className="text-sm font-semibold mt-2">{value}</p>
              </div>
            ))}
          </div>
          <button className="btn-accent text-sm mt-10 flex items-center gap-2" data-testid="host-cta" onClick={() => scrollToId("register")}>
            <Check className="w-4 h-4" strokeWidth={3} /> Reserve My Free Seat
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Transformation = () => (
  <section className="px-6 md:px-12 xl:px-20 py-28 md:py-36" data-testid="transformation-section">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
      <div>
        <SectionLabel num="006" text="Sound Familiar?" />
        <Reveal>
          <h2 className="mt-6 uppercase font-black tracking-tight text-3xl md:text-4xl leading-[1.05]">
            If You're Like Most Veterans & Active Duty Buyers I Work With...
          </h2>
          <div className="mt-8 space-y-5 text-white/55 leading-[1.8]">
            <p>
              ...you've probably already hit a wall or two. Maybe a lender quoted you like any other buyer and never mentioned
              your $0-down benefit. Maybe you were told "not yet" — save more, wait longer, come back when things are more
              stable — without anyone explaining what actually drives a VA approval.
            </p>
            <p>
              And the confusion compounds: entitlement, funding fees, residual income, COEs — everyone uses the words, nobody
              defines them. So you're left comparing four quotes that don't compare, unsure which factors matter for your
              situation and which are just noise. That fog is not a you-problem. It's an information problem — and it's fixable
              in 45 minutes.
            </p>
          </div>
        </Reveal>
      </div>
      <div>
        <Reveal delay={0.15}>
          <p className="font-mono-c text-[12px] tracking-[0.3em] text-[#5D8FC4] uppercase">Here's What Changes After This Webinar:</p>
          <ul className="mt-8 space-y-6">
            {TRANSFORMS.map((t, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`transform-bullet-${i + 1}`}>
                <ArrowRight className="w-5 h-5 text-[#5D8FC4] shrink-0 mt-1" strokeWidth={2.5} />
                <p className="text-base text-white/80 leading-relaxed">{t}</p>
              </li>
            ))}
          </ul>
          <button className="btn-accent text-sm mt-12 flex items-center gap-2" data-testid="transformation-cta" onClick={() => scrollToId("register")}>
            <Check className="w-4 h-4" strokeWidth={3} /> Reserve My Free Seat
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Takeaways = () => (
  <section className="px-6 md:px-12 xl:px-20 py-28 md:py-36 bg-white/[0.015]" data-testid="takeaways-section">
    <SectionLabel num="007" text="The Takeaways" />
    <Reveal>
      <h2 className="mt-6 uppercase font-black tracking-tight text-3xl md:text-5xl">What You'll Walk Away With</h2>
    </Reveal>
    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TAKEAWAYS.map((t, i) => {
        const Icon = TICONS[t.icon];
        return (
          <Reveal key={i} delay={i * 0.08}>
            <div className="audience-card p-7 h-full" data-testid={`takeaway-card-${i + 1}`}>
              <Icon className="w-7 h-7 text-[#5D8FC4]" strokeWidth={1.5} />
              <h3 className="mt-5 font-black uppercase text-base">{t.title}</h3>
              <p className="mt-3 text-sm text-white/50 leading-relaxed">{t.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const FaqItem = ({ num, q, a, open, onToggle, testId }) => (
  <div className="border-b border-white/10">
    <button onClick={onToggle} data-testid={testId} className="w-full flex items-center gap-5 py-5 text-left group">
      <span className="font-mono-c text-sm text-[#5D8FC4]">{num}</span>
      <span className={`flex-1 font-bold text-base transition-colors duration-300 ${open ? "text-[#5D8FC4]" : "group-hover:text-white"}`}>{q}</span>
      <Plus className={`w-5 h-5 text-[#5D8FC4] transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
    </button>
    <div className={`accordion-body ${open ? "open" : ""}`}>
      <div><p className="pb-6 pl-10 pr-4 text-sm text-white/55 leading-[1.75] max-w-2xl">{a}</p></div>
    </div>
  </div>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="px-6 md:px-12 xl:px-20 py-28 md:py-36" data-testid="faq-section">
      <SectionLabel num="008" text="Before You Ask" />
      <Reveal>
        <h2 className="mt-6 uppercase font-black tracking-tight text-3xl md:text-5xl">Common Questions</h2>
      </Reveal>
      <div className="mt-12 border-t border-white/10 max-w-4xl">
        {FAQS.map((f, i) => (
          <FaqItem key={i} num={String(i + 1).padStart(2, "0")} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} testId={`faq-accordion-${i + 1}`} />
        ))}
      </div>
    </section>
  );
};

export const FinalRegistration = () => (
  <section id="register" className="relative px-6 md:px-12 xl:px-20 py-28 md:py-40 bg-white/[0.01] overflow-hidden" data-testid="final-registration-section">
    <ParallaxBg opacity={0.16} />
    <div className="portal-ring hidden lg:block w-[620px] h-[620px] right-[-40px] top-1/2 -translate-y-1/2" aria-hidden="true" />
    <div className="portal-ring reverse hidden lg:block w-[760px] h-[760px] right-[-110px] top-1/2 -translate-y-1/2" aria-hidden="true" />
    <div className="absolute left-[-15%] top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(93,143,196,0.13) 0%, transparent 65%)" }} />
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <div>
        <SectionLabel num="009" text="Final Call" />
        <Reveal>
          <h2 className="mt-8 uppercase font-black tracking-[-0.02em] leading-[0.97] text-[clamp(36px,4.6vw,64px)]">
            Your Next Move Starts With a Clearer <span className="text-[#5D8FC4]">Map.</span>
          </h2>
          <ul className="mt-10 space-y-4">
            {[
              "45-minute live briefing — free, no cost ever",
              "No loan application, no hard credit pull to attend",
              "Live VA entitlement & purchasing power walkthrough",
              "Direct follow-up with Oscar — never a call center",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-white/70">
                <Check className="w-5 h-5 text-[#5D8FC4] shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[15px]">{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
      <div className="w-full max-w-md lg:justify-self-end">
        <Reveal delay={0.15}>
          <RegistrationForm idPrefix="final" />
        </Reveal>
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="relative border-t border-white/10 px-6 md:px-12 xl:px-20 pt-20 pb-40 md:pb-48 overflow-hidden" data-testid="footer-section">
    <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div className="text-sm text-white/45 space-y-1.5">
        <img src={LOGO_URL} alt="Great Western Funding" className="brand-logo h-12 w-auto mb-5" data-testid="footer-logo" />
        <p className="font-bold text-[#EDF2F7]">{HOST.name}</p>
        <p>{HOST.company}</p>
        <p>{HOST.nmls}</p>
        <p>Company NMLS# 2739318</p>
        <p>Licensed in {HOST.states}</p>
      </div>
      <div className="text-sm text-white/45 space-y-1.5">
        <p className="font-mono-c text-[10px] tracking-[0.25em] text-white/30 uppercase mb-3">Contact</p>
        <p>{HOST.phone}</p>
        <p>oscar@greatwesternfunding.com</p>
        <p>greatwesternfunding.com</p>
      </div>
      <div className="text-sm text-white/45 space-y-1.5">
        <p className="font-mono-c text-[10px] tracking-[0.25em] text-white/30 uppercase mb-3">Legal</p>
        <p>
          <Link to="/privacy-policy" className="hover:text-[#5D8FC4] transition-colors duration-200 underline decoration-white/20" data-testid="footer-privacy-policy-link">
            Privacy Policy
          </Link>
        </p>
        <p>
          <Link to="/terms-conditions" className="hover:text-[#5D8FC4] transition-colors duration-200 underline decoration-white/20" data-testid="footer-terms-conditions-link">
            Terms & Conditions
          </Link>
        </p>
      </div>
    </div>
    <div className="relative z-10 mt-14 flex items-start gap-4 max-w-4xl">
      <House className="w-6 h-6 text-white/30 shrink-0 mt-1" strokeWidth={1.5} />
      <p className="text-[11px] text-white/30 leading-[1.8]" data-testid="compliance-disclosure">{COMPLIANCE}</p>
    </div>
    <p className="relative z-10 font-mono-c text-[10px] text-white/[0.18] mt-10 tracking-widest" data-testid="copyright-line">
      © 2026 Great Western Funding · WAM.WEBINAR v2.0
    </p>
    <div className="footer-wordmark" aria-hidden="true">VA PURCHASING POWER</div>
  </footer>
);
